import { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { MathEnabledTextComponent, SecondaryButton, TextLinkButton, styleVariables } from '@lib/common';
import { Space } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { hasEssayContent } from './essay-text-metrics';
import { EssayAnswerMetrics } from './essay-answer-metrics.component';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var ANSWER_FONT_SIZE_PX = 14;
var ANSWER_LINE_HEIGHT = 1.5;
var MAX_COLLAPSED_LINES = 10;
var DEFAULT_COLLAPSED_HEIGHT = ANSWER_FONT_SIZE_PX * ANSWER_LINE_HEIGHT * MAX_COLLAPSED_LINES;
var DEFAULT_EXPANDED_HEIGHT = 480;
var StyledTruncatedText = styled.div.withConfig({
  displayName: "essay-answer-editor-previewcomponent__StyledTruncatedText",
  componentId: "sc-nfvsvs-0"
})(["overflow:hidden;text-overflow:ellipsis;height:", ";overflow-y:", ";padding-right:", ";box-sizing:border-box;scrollbar-gutter:stable;transition:height 320ms ease-in-out;@media (prefers-reduced-motion:reduce){transition:none;}"], function (_ref) {
  var $isShortView = _ref.$isShortView,
      $collapsedHeight = _ref.$collapsedHeight,
      $expandedHeight = _ref.$expandedHeight,
      $hasOverflow = _ref.$hasOverflow;
  return $hasOverflow ? "".concat($isShortView ? $collapsedHeight : $expandedHeight, "px") : 'auto';
}, function (_ref2) {
  var $isShortView = _ref2.$isShortView,
      $hasOverflow = _ref2.$hasOverflow;
  return !$isShortView && $hasOverflow ? 'auto' : 'hidden';
}, function (_ref3) {
  var $hasOverflow = _ref3.$hasOverflow;
  return $hasOverflow ? '12px' : '0';
});
var StyledAnswerHeader = styled.div.withConfig({
  displayName: "essay-answer-editor-previewcomponent__StyledAnswerHeader",
  componentId: "sc-nfvsvs-1"
})(["display:flex;align-items:center;flex-wrap:wrap;gap:12px;justify-content:space-between;"]);
var answerContentEnter = keyframes(["from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}"]);
var StyledAnswerTitle = styled.span.withConfig({
  displayName: "essay-answer-editor-previewcomponent__StyledAnswerTitle",
  componentId: "sc-nfvsvs-2"
})(["color:", ";font-size:14px;font-weight:700;line-height:20px;"], styleVariables.colors.grey100);
var StyledAnswerHeaderActions = styled.div.withConfig({
  displayName: "essay-answer-editor-previewcomponent__StyledAnswerHeaderActions",
  componentId: "sc-nfvsvs-3"
})(["display:flex;align-items:center;flex-wrap:wrap;gap:12px;margin-left:auto;"]);
var StyledEditButton = styled(SecondaryButton).withConfig({
  displayName: "essay-answer-editor-previewcomponent__StyledEditButton",
  componentId: "sc-nfvsvs-4"
})(["display:inline-flex;align-items:center;gap:8px;box-shadow:0 2px 4px ", ";"], styleVariables.colors.transparentGray6);
var StyledAnswerSurface = styled.div.withConfig({
  displayName: "essay-answer-editor-previewcomponent__StyledAnswerSurface",
  componentId: "sc-nfvsvs-5"
})(["position:relative;min-height:180px;overflow:hidden;border:2px solid #c7d5e7;border-radius:", ";background:", ";padding:24px;box-shadow:0 2px 6px ", ";animation:", " 180ms ease-out;@media (prefers-reduced-motion:reduce){animation:none;}"], styleVariables.borderRadius.medium, styleVariables.colors.gray1, styleVariables.colors.transparentGray6, answerContentEnter);
var StyledAnswerText = styled(StyledTruncatedText).withConfig({
  displayName: "essay-answer-editor-previewcomponent__StyledAnswerText",
  componentId: "sc-nfvsvs-6"
})(["color:", ";font-size:", "px;line-height:", ";img{max-width:100%;}"], styleVariables.colors.grey100, ANSWER_FONT_SIZE_PX, ANSWER_LINE_HEIGHT);
var StyledAnswerPlaceholder = styled.div.withConfig({
  displayName: "essay-answer-editor-previewcomponent__StyledAnswerPlaceholder",
  componentId: "sc-nfvsvs-7"
})(["color:", ";font-size:16px;font-style:italic;"], styleVariables.colors.grey70);
var StyledAnswerMore = styled.div.withConfig({
  displayName: "essay-answer-editor-previewcomponent__StyledAnswerMore",
  componentId: "sc-nfvsvs-8"
})(["display:flex;justify-content:flex-end;min-height:40px;margin-top:8px;"]);
export var EssayAnswerEditorPreviewComponent = function EssayAnswerEditorPreviewComponent(_ref4) {
  var _ref4$value = _ref4.value,
      value = _ref4$value === void 0 ? '' : _ref4$value,
      _ref4$maxHeight = _ref4.maxHeight,
      maxHeight = _ref4$maxHeight === void 0 ? DEFAULT_COLLAPSED_HEIGHT : _ref4$maxHeight,
      _ref4$expandedHeight = _ref4.expandedHeight,
      expandedHeight = _ref4$expandedHeight === void 0 ? DEFAULT_EXPANDED_HEIGHT : _ref4$expandedHeight,
      onChangeEditMode = _ref4.onChangeEditMode,
      label = _ref4.label,
      _ref4$canEdit = _ref4.canEdit,
      canEdit = _ref4$canEdit === void 0 ? true : _ref4$canEdit;
  var divRef = useRef(null);

  var _useState = useState(true),
      isShortView = _useState[0],
      setIsShortView = _useState[1];

  var _useState2 = useState(0),
      height = _useState2[0],
      setHeight = _useState2[1];

  var hasOverflow = height > maxHeight;
  useEffect(function () {
    if (!divRef.current) return;
    var resizeObserver = new ResizeObserver(function () {
      var _divRef$current;

      setHeight(((_divRef$current = divRef.current) === null || _divRef$current === void 0 ? void 0 : _divRef$current.scrollHeight) || 0);
    });
    resizeObserver.observe(divRef.current);
    return function () {
      return resizeObserver.disconnect();
    };
  }, [value]);

  var renderMoreButton = function renderMoreButton() {
    if (!hasOverflow) {
      return null;
    }

    var toggleView = function toggleView() {
      return setIsShortView(!isShortView);
    };

    var buttonText = isShortView ? label.seeMore : label.seeLess;
    return /*#__PURE__*/_jsx(TextLinkButton, {
      "aria-expanded": !isShortView,
      onClick: toggleView,
      children: buttonText
    });
  };

  return /*#__PURE__*/_jsxs(Space, {
    direction: "vertical",
    size: 16,
    style: {
      width: '100%'
    },
    children: [/*#__PURE__*/_jsxs(StyledAnswerHeader, {
      children: [/*#__PURE__*/_jsx(StyledAnswerTitle, {
        children: label.yourAnswer || 'Your Answer'
      }), /*#__PURE__*/_jsxs(StyledAnswerHeaderActions, {
        children: [/*#__PURE__*/_jsx(EssayAnswerMetrics, {
          value: value,
          label: label
        }), canEdit && /*#__PURE__*/_jsx(StyledEditButton, {
          icon: /*#__PURE__*/_jsx(EditOutlined, {}),
          onClick: onChangeEditMode,
          children: label.edit || 'Edit'
        })]
      })]
    }), /*#__PURE__*/_jsxs(StyledAnswerSurface, {
      children: [/*#__PURE__*/_jsx(StyledAnswerText, {
        ref: divRef,
        $isShortView: isShortView,
        $collapsedHeight: maxHeight,
        $expandedHeight: expandedHeight,
        $hasOverflow: hasOverflow,
        children: hasEssayContent(value) ? /*#__PURE__*/_jsx(MathEnabledTextComponent, {
          value: value
        }) : /*#__PURE__*/_jsx(StyledAnswerPlaceholder, {
          children: label.inputAnswer
        })
      }), hasOverflow && /*#__PURE__*/_jsx(StyledAnswerMore, {
        children: renderMoreButton()
      })]
    })]
  });
};